import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import {
  useOrderLogicRules,
  useUpdateOrderLogicRule,
  useDeleteOrderLogicRule,
} from '../../hooks/useOrderLogicRules';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import type { OrderLogicRule } from '../../api/orderLogicRules';

const columnHelper = createColumnHelper<OrderLogicRule>();

export default function OrderLogicRulesPage() {
  const navigate = useNavigate();
  const { data: rules, isLoading } = useOrderLogicRules();
  const updateRule = useUpdateOrderLogicRule();
  const deleteRule = useDeleteOrderLogicRule();
  const [deleteTarget, setDeleteTarget] = useState<OrderLogicRule | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => (
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('trigger', {
        header: 'Trigger',
        cell: (info) => (
          <span className="badge bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
            {info.getValue().replace(/^on_/, '').replace(/_/g, ' ')}
          </span>
        ),
      }),
      columnHelper.accessor('enabled', {
        header: 'Enabled',
        cell: (info) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              updateRule.mutate({ urn: info.row.original.urn, data: { enabled: !info.getValue() } });
            }}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            {info.getValue() ? (
              <ToggleRight className="h-5 w-5 text-green-500" />
            ) : (
              <ToggleLeft className="h-5 w-5 text-gray-400" />
            )}
          </button>
        ),
      }),
      columnHelper.accessor('priority', {
        header: 'Priority',
        cell: (info) => <span className="text-sm dark:text-gray-200">{info.getValue()}</span>,
      }),
      columnHelper.accessor('applicableLenders', {
        header: 'Lenders',
        cell: (info) => (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {info.getValue()?.length || 'All'}
          </span>
        ),
      }),
      columnHelper.accessor('applicableProducts', {
        header: 'Products',
        cell: (info) => (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {info.getValue()?.length || 'All'}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => (
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/rules/order-logic/${encodeURIComponent(info.row.original.urn)}`);
              }}
              className="btn-icon"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(info.row.original);
              }}
              className="btn-icon text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      }),
    ],
    [updateRule, navigate],
  );

  return (
    <div>
      <PageHeader
        title="Order Logic Rules"
        description="Rules that automatically create derived orders based on order lifecycle events"
        actions={
          <button
            onClick={() => navigate('/rules/order-logic/new')}
            className="btn-primary gap-1"
          >
            <Plus className="h-4 w-4" />
            New Rule
          </button>
        }
      />

      <DataTable data={rules || []} columns={columns} isLoading={isLoading} />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Rule"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (deleteTarget) {
            deleteRule.mutate(deleteTarget.urn);
            setDeleteTarget(null);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
